import { DateTime } from 'luxon'

import {
  DATE_FORMATS_LUXON,
  convertUtcToLocalDate,
  isoToUTCDateTime,
} from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'

import {
  selectCurrentAnnualTaxYear,
  selectTaxDetailsByYear,
} from '@/featuresV1/Admin/AnnualTaxDetails/annualTaxDetails.selector'
import { selectHasIncompleteExtension } from '@/featuresV1/Taxes/AnnualTaxes/annualTaxFilings.selector'
import { select1120sFormForYear } from '@/featuresV1/Taxes/AnnualTaxes/annualTaxFilingForms.selector'
import { AnnualTaxFilingFormQuestionnaireStatus } from '@/featuresV1/Taxes/AnnualTaxes/annualTaxFilingForms.slice'
import { UserActionItem } from '../lib/userActionItems.slice'
import {
  UserActionItemTabPane,
  ACTION_ITEM_TYPE,
} from '../UserActionItemTabPane'

import { TypographyStyles } from '@/styles'
import { Badge, Card, CardColors, Text } from '@/ui'

interface TaxActionItemsCardProps {
  incompleteActionItems: UserActionItem[]
  isUrgent?: boolean
}

export const TaxActionItemsCard = ({
  incompleteActionItems,
  isUrgent,
}: TaxActionItemsCardProps) => {
  const taxYear = useReselector(selectCurrentAnnualTaxYear)
  const hasIncompleteExtension = useReselector(
    selectHasIncompleteExtension,
    taxYear
  )

  const annualTaxDetails = useReselector(selectTaxDetailsByYear, taxYear)

  const form1120s = useReselector(select1120sFormForYear, taxYear)
  const form1120sTQExtendedDueDate = convertUtcToLocalDate(
    annualTaxDetails?.form_1120_s_tq_extended_due_date
  )
  const hasIncomplete1120s =
    form1120s &&
    form1120s.questionnaireResponseStatus !==
      AnnualTaxFilingFormQuestionnaireStatus.submitted &&
    annualTaxDetails &&
    //add one day to be inclusive of the cutoff date
    form1120sTQExtendedDueDate &&
    DateTime.now() <= form1120sTQExtendedDueDate.endOf('day')

  return (
    <Card className="min-h-[300px]" color={CardColors.LEVEL_0}>
      <div className="flex flex-col space-y-4">
        <div className="flex items-center">
          {isUrgent && (
            <Badge color="red" className="mr-2.5">
              Urgent
            </Badge>
          )}

          <Text variant={TypographyStyles.H2}>
            {hasIncompleteExtension
              ? 'Complete Your Extension Request Survey'
              : hasIncomplete1120s &&
                  annualTaxDetails?.form_1120_s_tq_extended_due_date
                ? `Complete your Business Tax Checklist by ${isoToUTCDateTime(annualTaxDetails.form_1120_s_tq_extended_due_date).toFormat(DATE_FORMATS_LUXON.MONTH_DAY_LONG)}`
                : 'Get Started With Your Tax Checklist'}
          </Text>
        </div>

        <Text>
          {hasIncompleteExtension
            ? 'The Extension Request Survey provides all the necessary information to connect you with your tax preparer and for them to file your extension request. The sooner you complete it, the sooner we can file your extension.'
            : hasIncomplete1120s &&
                annualTaxDetails?.form_1120_s_tq_extended_due_date
              ? `You must complete your Business Tax Checklist by ${isoToUTCDateTime(annualTaxDetails.form_1120_s_tq_extended_due_date).toFormat(DATE_FORMATS_LUXON.MONTH_DAY_LONG)}, or you will not be able to file Business or Personal taxes with Heard for the ${taxYear} Tax Season.`
              : 'The Tax Checklist includes all of the information we need from you to ensure an on-time and accurate tax filing experience. The sooner you complete it, the sooner we can start working on your tax return(s).'}
        </Text>

        <UserActionItemTabPane
          groupOption={'date-asc-groupedby-categoryName'}
          tabPaneType={ACTION_ITEM_TYPE.ACTIVE}
          userActionItems={incompleteActionItems}
          hideCalendar
        />
      </div>
    </Card>
  )
}
